import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';

import store from '../store';
import AnalyticsProvider from '../components/analytics/AnalyticsProvider';

import { updateHome } from '../actions/homes';

import Storage from '../utils/Storage';

import ExchangesReviewsInfo from '../components/home/exchanges-reviews-info';
import HomeLoader from '../components/home/HomeLoader';
import HomeDetail from '../components/home/HomeDetail';
import HomeHeader from '../components/home/HomeHeader';
import ContactButton from '../components/home/ContactButton';
import HomeDisconnectedActions from '../components/home/HomeDisconnectedActions';
import HomeDescription from '../components/home/HomeDescription';
import HomeViewCalendar from '../components/home/HomeViewCalendar';
import CalendarCaptionUpdate from '../components/calendar/CalendarCaptionUpdate';
import HomeViewCarousel from '../components/home/HomeViewCarousel';
import PreferredDestinations from '../components/home/PreferredDestinations';
import TravelingGroupInfo from '../components/home/TravelingGroupInfo';
import HomeRating from '../components/rating/HomeRating';
import HomeAggregateRating from '../components/rating/HomeAggregateRating';
import VerificationIndicators from '../components/user/verification/VerificationIndicators';
import HomeMap from '../components/home/HomeMap';
import Api from '../api/Api';
import HomepageNewest from '../components/homepage/HomepageNewest';
import UserSponsor from '../components/home/UserSponsor';
import UserAmbassador from '../components/common/ambassador/UserAmbassador';
import UserSponsorBadge from '../components/home/UserSponsorBadge';
import ResponseTime from '../components/user/ResponseTime';
import NewUserLabel from '../components/user/view/NewUserLabel';
import i18n from '../i18n';
import UserBadge from '../components/home/user-badge';
import HomeFeatures from '../components/home/HomeFeatures';
import useBadge from '../hooks/useBadge';
import Loading from '../components/common/Loading';
import { ReportHomeAbuse } from '../components/home/abuse-ts';
import ReactivityLevelHome from '../components/home/ReactivityLevelHome';
import BecomeMemberSection from '../components/home/BecomeMemberSection';
import { CurrencyProvider } from '../components/currency';
import { ToastProvider } from '@homeexchange/design';

const isLogged = document.getElementsByClassName('layout-login').length > 0;
const homeView = document.getElementById('home-view');
const homeId = homeView ? parseInt(homeView.getAttribute('data-home'), 10) : null;
const userId = homeView ? parseInt(homeView.getAttribute('data-user'), 10) : null;
import { enableMocking } from '../mocks/enableMock';
import CollectionMemberBadge from '../components/user/view/CollectionMemberBadge';
import useMemberCollection from '../hooks/useMemberCollection';

// Fetch data
const root = createRoot(document.createElement('div'));
enableMocking().then(() =>
    root.render(
        <Provider store={store}>
            <HomeLoader userId={userId} homeId={homeId} isLogged={isLogged} />
        </Provider>
    )
);

const homeDetail = document.getElementById('home-detail');
if (homeDetail) {
    createRoot(homeDetail).render(
        <Provider store={store}>
            <HomeDetail />
        </Provider>
    );
}

const homeFeatures = document.getElementById('home-features');
if (homeFeatures) {
    createRoot(homeFeatures).render(
        <Provider store={store}>
            <HomeFeatures homeId={homeId} />
        </Provider>
    );
}

const homeHeader = document.getElementById('home-header-react');
if (homeHeader) {
    createRoot(homeHeader).render(
        <Provider store={store}>
            <HomeHeader homeId={homeId} />
        </Provider>
    );
}

const userBadge = document.querySelectorAll('.user-badge');
if (userBadge) {
    const UserBadgeHomeView = () => {
        const { level, isLoading: loadingBadge } = useBadge(userId);
        const { isMember, isLoading: loadingMember } = useMemberCollection(userId);

        if (loadingBadge || loadingMember) {
            return <Loading />;
        }

        return (
            <Provider store={store}>
                {isMember && (
                    <div className="m-t-20">
                        <CollectionMemberBadge />
                    </div>
                )}

                <UserBadge level={level} />
            </Provider>
        );
    };

    [].forEach.call(userBadge, (element) => {
        createRoot(element).render(<UserBadgeHomeView />);
    });
}

const userExchangeReviews = document.getElementById('user-exchange-reviews');
if (userExchangeReviews) {
    const root = createRoot(userExchangeReviews);
    root.render(
        <Provider store={store}>
            <ExchangesReviewsInfo userId={userId} />
        </Provider>
    );
}

// Initialize Verification Indicators
const userVerificationIndicators = document.getElementById('user-verification-indicators');
if (userVerificationIndicators) {
    createRoot(userVerificationIndicators).render(
        <Provider store={store}>
            <VerificationIndicators userId={userId} withDivider={false} />
        </Provider>
    );
}

const userVerificationIndicatorsResponsive = document.getElementById(
    'user-verification-indicators-responsive'
);
if (userVerificationIndicatorsResponsive) {
    createRoot(userVerificationIndicatorsResponsive).render(
        <Provider store={store}>
            <VerificationIndicators userId={userId} withDivider={false} />
        </Provider>
    );
}

enableMocking().then(() => {
    if (isLogged) {
        Api.ResponseTime.fetchResponseTime(userId)
            .then((response) => {
                const { response_time: responseTime, is_new_user: isNewUser } = response;
                const responseTimeContainer = document.querySelectorAll('.user-response-time');
                const newUserLabelContainer = document.querySelectorAll('.new-user-label');

                if (isNewUser) {
                    newUserLabelContainer.forEach((element) => {
                        createRoot(element).render(<NewUserLabel label={i18n.t('common:new-user')} />);
                    });
                } else {
                    newUserLabelContainer.forEach((element) => element.remove());
                }

                if (!isNewUser && responseTime) {
                    responseTimeContainer.forEach((element) => {
                        createRoot(element).render(
                            <ResponseTime responseTime={response} isHomeView={true} />
                        );
                    });
                } else {
                    responseTimeContainer.forEach((element) => element.remove());
                }
            })
            .catch((error) => {
                console.error(error.responseJSON);
            });
    }
});

const responseRateContainer = document.querySelectorAll('.user-response-rate');
if (responseRateContainer) {
    responseRateContainer.forEach((element) => {
        createRoot(element).render(
            <Provider store={store}>
                <ReactivityLevelHome userId={userId} />
            </Provider>
        );
    });
} else {
    responseRateContainer.forEach((element) => element.remove());
}

// Initialize Carousel
const homeViewCarouselElements = document.querySelectorAll('.home-view-carousel');
[].forEach.call(homeViewCarouselElements, (element) => {
    createRoot(element).render(
        <Provider store={store}>
            <HomeViewCarousel homeId={homeId} />
        </Provider>
    );
});
const calendarView = document.getElementById('calendar-view');
if (calendarView && isLogged) {
    createRoot(calendarView).render(
        <Provider store={store}>
            <HomeViewCalendar homeId={homeId} />
        </Provider>
    );
}

const calendarUpdateView = document.getElementById('home-view-calendar-caption-update');
if (calendarUpdateView && isLogged) {
    createRoot(calendarUpdateView).render(
        <Provider store={store}>
            <CalendarCaptionUpdate homeId={homeId} />
        </Provider>
    );
}

// Initialize Home Description
const homeViewDescription = document.getElementById('home_description');
if (homeViewDescription) {
    createRoot(homeViewDescription).render(
        <Provider store={store}>
            <HomeDescription homeId={homeId} />
        </Provider>
    );
}

// Initialize Rating
const homeRating = document.getElementById('home_rating_react');
if (homeRating) {
    createRoot(homeRating).render(
        <Provider store={store}>
            <HomeRating homeId={homeId} />
        </Provider>
    );
}

// Initialize Rating
const aggregateRatings = document.getElementsByClassName('aggregate-rating');
if (aggregateRatings) {
    Array.prototype.forEach.call(aggregateRatings, (aggregateRating) => {
        createRoot(aggregateRating).render(
            <Provider store={store}>
                <HomeAggregateRating homeId={homeId} />
            </Provider>
        );
    });
}

// Contact buttons
const contactButtons = document.querySelectorAll('.contact-buttons');
[].forEach.call(contactButtons, (element) => {
    createRoot(element).render(
        <Provider store={store}>
            <ToastProvider>
                <AnalyticsProvider>
                    <ContactButton homeId={homeId} patchHome={updateHome} />
                </AnalyticsProvider>
            </ToastProvider>
        </Provider>
    );
});

// Disconnected actions (Contact / Add to favorites / Share)
const disconnectedActions = document.querySelectorAll('.home-disconnected-actions');
[].forEach.call(disconnectedActions, (element) => {
    const useSigninForContact = element.getAttribute('data-use-signin') === '1';
    createRoot(element).render(
        <Provider store={store}>
            <ToastProvider>
                <HomeDisconnectedActions homeId={homeId} useSigninForContact={useSigninForContact} />
            </ToastProvider>
        </Provider>
    );
});

// User ambassador
const userAmbassadorContainers = document.getElementsByClassName('user-ambassador');
if (userAmbassadorContainers) {
    [].forEach.call(userAmbassadorContainers, (container) => {
        createRoot(container).render(
            <Provider store={store}>
                <UserAmbassador />
            </Provider>
        );
    });
}

// User sponsor
const userSponsorContainer = document.getElementById('user-sponsor');
if (userSponsorContainer) {
    createRoot(userSponsorContainer).render(
        <Provider store={store}>
            <UserSponsor />
        </Provider>
    );
}

// User sponsor badge
const userSponsorBadgeContainer = document.querySelectorAll('.user-sponsor-badge-container');
[].forEach.call(userSponsorBadgeContainer, (element) => {
    if (userSponsorBadgeContainer.length > 0) {
        createRoot(element).render(
            <Provider store={store}>
                <UserSponsorBadge />
            </Provider>
        );
    }
});

const travelersContainer = document.querySelectorAll('.home-view-travelers-info');
if (travelersContainer.length > 0) {
    Api.Traveler.fetchTravelersComposition(userId).then((response) => {
        [].forEach.call(travelersContainer, (element) => {
            createRoot(element).render(
                <Provider store={store}>
                    <TravelingGroupInfo userId={userId} composition={response} />
                </Provider>
            );
        });
    });
}
const preferredDestinations = document.querySelectorAll('.preferred-destinations');
if (preferredDestinations.length > 0) {
    [].forEach.call(preferredDestinations, (element) => {
        createRoot(element).render(
            <Provider store={store}>
                <PreferredDestinations userId={userId} />
            </Provider>
        );
    });
}

const reportHomeAbuse = document.getElementById('report-home-abuse');
if (reportHomeAbuse) {
    createRoot(reportHomeAbuse).render(
        <Provider store={store}>
            <ReportHomeAbuse homeId={homeId} userId={userId} />
        </Provider>
    );
}

const mapContainer = document.getElementById('map-container');
if (mapContainer) {
    createRoot(mapContainer).render(
        <Provider store={store}>
            <HomeMap homeId={homeId} />
        </Provider>
    );
}

const similarHomes = document.getElementById('similar-homes-react');
if (similarHomes && isLogged) {
    createRoot(similarHomes).render(
        <Provider store={store}>
            <ToastProvider>
                <HomepageNewest isLogged={isLogged} isSimilarHome={true} homeId={homeId} userId={userId} />
            </ToastProvider>
        </Provider>
    );
}

const becomeMemberSections = document.querySelectorAll('.become-member-section');
[].forEach.call(becomeMemberSections, (element) => {
    createRoot(element).render(
        <Provider store={store}>
            <CurrencyProvider>
                <BecomeMemberSection />
            </CurrencyProvider>
        </Provider>
    );
});

// Add home to the list of last viewed homes
Storage.addItem('homes_history', homeId, 'local', true, true);

// Track sticky bottom height to offset floating widgets (e.g. ADA chat button)
const stickyBottom = document.getElementById('home-view-sticky-bottom');
if (stickyBottom) {
    const updateStickyHeight = () => {
        document.body.style.setProperty('--home-view-sticky-bottom-height', `${stickyBottom.offsetHeight}px`);
    };
    updateStickyHeight();
    if (typeof ResizeObserver !== 'undefined') {
        new ResizeObserver(updateStickyHeight).observe(stickyBottom);
    }
}
